import {AreaNode, PlantNode, TreeNode, UpmEntityType} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit'
import {Loader} from '@hconnect/uikit/src/lib2'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import {Box, Button, Stack, TextField} from '@mui/material'
import {UseQueryResult} from '@tanstack/react-query'
import {debounce} from 'lodash'
import {ElementType, useEffect, useMemo, useRef, useState} from 'react'
import {NodeRendererProps, Tree, TreeApi} from 'react-arborist'
import {useTranslation} from 'react-i18next'
import {useNavigate, useSearchParams} from 'react-router'
import {useLocalStorage} from 'react-use'
import useResizeObserver from 'use-resize-observer'

import {Switch} from '../../common/components'
import {PlantStructureFilterOptions} from '../../common/enums'
import {useGetPlantStructure} from '../../common/hooks/queries/usePlantStructureQuery'
import {getUrl, PLANT_ROUTES, useUrlParam} from '../../routing'
import {useGetPlantUpmId} from '../hooks/upmEntityConfig/useGetPlantUpmId'
import {useUpmPlantDetails} from '../hooks/upmEntityConfig/useUpmPlantDetails'
import {useItemDetails, useSelectedItem} from '../providers'

import {AddAreaPopup} from './AddAreaPopup'
import {AddDepartmentPopup} from './AddDepartmentPopup'
import {AddSubdomain} from './AddSubdomain'
import {DeleteDepartmentPopup} from './DeleteDepartmentPopup'
import {DeleteEquipmentPopup} from './DeleteEquipmentPopup'
import {useHandleTreeFiltering} from './hooks'
import {PlantStructureNodeExtraProps} from './PlantStructureNode'
import {PlantStructureTreePopupWrapper} from './PlantStructureTreePopupWrapper'
import {RestoreDepartmentPopup} from './RestoreSetupTree/RestoreDepartmentPopup'
import {RestoreEquipmentPopup} from './RestoreSetupTree/RestoreEquipmentPopup'

const PLANT_SETUP_ENGLISH_TOGGLE_KEY = 'plantSetupEnglishTranslation'
const getPlantStructureProductFilter = (selectedFilterOption: PlantStructureFilterOptions) => {
  switch (selectedFilterOption) {
    case PlantStructureFilterOptions.Janus:
      return PlantStructureFilterOptions.Janus
    default:
      return undefined
  }
}

interface PlantStructureTreeProps {
  selectedFilterOption: PlantStructureFilterOptions
  hook?: (data: {
    includeEquipment: boolean
    product?: string
    upmId?: string | undefined
    showEnglishTranslation?: boolean
  }) => UseQueryResult<PlantNode, Error>

  nodeComponent: ElementType<NodeRendererProps<TreeNode>> | undefined
}

type NodeComponentWithInjectedProps = ElementType<
  NodeRendererProps<TreeNode> & Partial<PlantStructureNodeExtraProps>
>

export const PlantStructureTree: React.FC<PlantStructureTreeProps> = ({
  selectedFilterOption,
  hook = useGetPlantStructure,
  nodeComponent
}) => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {setSelectedItem, setUpmPlantId, selectionGuardRef} = useSelectedItem()
  const englishToggleStorageKey = useMemo(() => {
    return PLANT_SETUP_ENGLISH_TOGGLE_KEY
  }, [])
  const [storedEnglishToggle, setStoredEnglishToggle] = useLocalStorage<boolean>(
    englishToggleStorageKey,
    false
  )

  const {
    editedArea,
    addedArea,
    departmentToDelete,
    equipmentToDelete,
    departmentToRestore,
    equipmentToRestore
  } = useItemDetails()

  const plantCode = useUrlParam('plantCode')
  const nodeIdParam = useUrlParam('nodeId')

  // Text filter and equipment visibility
  const [filterText, setFilterText] = useState<string>('')
  const debouncedSetFilterText = debounce((text: string) => setFilterText(text), 300)

  const [includeEquipment, setIncludeEquipment] = useState<boolean>(true)
  const showEnglishTranslation = storedEnglishToggle ?? false

  const productFilter = useMemo(
    () => getPlantStructureProductFilter(selectedFilterOption),
    [selectedFilterOption]
  )

  const {data: upmInfo} = useGetPlantUpmId()
  const upmIdNeededForAddOperations = useUpmPlantDetails(upmInfo?.upmId)

  const allowManualUpmCreation =
    useCommonFeatureFlag(FeatureFlagKey.AllowManualUpmCreation, {
      plantId: plantCode
    }) ?? false

  // Queries
  const {data: plantStructure} = hook({
    includeEquipment:
      includeEquipment && selectedFilterOption !== PlantStructureFilterOptions.Janus,
    product: productFilter,
    upmId: upmInfo?.upmId,
    showEnglishTranslation: showEnglishTranslation
  })

  // // needed for later filtering the already existing areas
  const getAreaTypes = (array: AreaNode[], fieldOf: UpmEntityType, fieldName: string | number) => {
    return array.filter((a) => a.itemType === fieldOf).map((a) => a[fieldName])
  }

  const existingAreas = plantStructure?.children
    ? getAreaTypes(plantStructure?.children, UpmEntityType.Area, 'text')
    : []

  // Plant structure tree
  const treeRef = useRef<TreeApi<TreeNode>>()
  const tree = treeRef.current

  const {ref: treeContainerRef, width, height} = useResizeObserver()
  const arboristTree = useMemo(() => (plantStructure ? [plantStructure] : []), [plantStructure])
  const wrappedNodeComponent = useMemo(() => {
    if (!nodeComponent) return undefined

    const NodeComponent = nodeComponent as NodeComponentWithInjectedProps

    return (props: NodeRendererProps<TreeNode>) => (
      <NodeComponent {...props} allowManualUpmCreation={allowManualUpmCreation} />
    )
  }, [allowManualUpmCreation, nodeComponent])

  const {searchTerm, handleTreeFiltering} = useHandleTreeFiltering(
    selectedFilterOption,
    filterText,
    showEnglishTranslation
  )

  const plantId: string = plantStructure?.upmId ?? ''

  useEffect(() => {
    if (plantStructure) {
      setUpmPlantId(plantStructure?.upmId)
    }
  }, [plantStructure, setUpmPlantId])

  // If selected node is present in the tree --> set selected item accordingly,
  // otherwise select the first node
  useEffect(() => {
    if (!tree) return
    const selectedNode = tree.selectedNodes[0]
    if (selectedNode) {
      if (!selectionGuardRef.current) {
        selectedNode.data.level = selectedNode.level
        setSelectedItem(selectedNode.data)
      }
    } else {
      tree.firstNode.data.level = tree.firstNode.level
      setSelectedItem(tree.firstNode.data)
      const targetUrl = getUrl(PLANT_ROUTES.PLANT_SETUP.NODE.path, {
        plantCode,
        nodeId: tree.firstNode.data.upmId
      })
      const searchParamsString = searchParams.toString()
      const urlWithParams = searchParamsString ? `${targetUrl}?${searchParamsString}` : targetUrl
      void navigate(urlWithParams)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, tree?.selectedNodes])

  const expandCollapseBatched = (
    api: TreeApi<TreeNode>,
    {
      batchSize = 200,
      open = true,
      excludeIds = []
    }: {batchSize?: number; open?: boolean; excludeIds?: string[]} = {}
  ): Promise<void> => {
    if (!api) return Promise.resolve()

    return new Promise((resolve) => {
      const processed = new Set<string>()

      const toggleBatch = () => {
        const nodes =
          api.visibleNodes?.filter(
            (n) => !n.isLeaf && !excludeIds.includes(n.id) && !processed.has(n.id)
          ) ?? []

        if (!nodes.length) {
          resolve()
          return
        }

        const batch = nodes.slice(0, batchSize)
        for (const node of batch) {
          processed.add(node.id)
          open ? node.open() : node.close()
        }

        setTimeout(toggleBatch, 0)
      }

      toggleBatch()
    })
  }

  if (!plantStructure) {
    return <Loader />
  }

  return (
    <>
      <Box>
        <Stack
          direction="column"
          justifyContent="space-between"
          {...dataTestId('janus-plant-structure-menu-list')}
          alignItems={'left'}
          gap={2}
        >
          <Box px={2}>
            <TextField
              fullWidth
              label={t('plantSetup.freeTextFilter')}
              variant="filled"
              onChange={(e) => debouncedSetFilterText(e.target.value)}
              {...dataTestId('plant-structure-text-filter')}
            />
          </Box>
          <Box display="flex" gap={1} px={2} flexWrap={'wrap'} justifyContent="space-between">
            <Box>
              <Switch
                value={showEnglishTranslation}
                onChange={setStoredEnglishToggle}
                disabled={false}
                label={t('plantSetup.action.englishTranslationToggle')}
                {...dataTestId('english_translation_toggle')}
              />
            </Box>
            <Box alignContent={'center'}>
              {selectedFilterOption !== PlantStructureFilterOptions.Janus && (
                <Button
                  onClick={() => setIncludeEquipment((prev) => !prev)}
                  size="small"
                  {...dataTestId('show-hide-equipments-button')}
                  variant="text"
                  startIcon={includeEquipment ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  sx={{p: 0.5}}
                >
                  {includeEquipment
                    ? t('plantSetup.action.hideEquipment')
                    : t('plantSetup.action.showEquipment')}
                </Button>
              )}
            </Box>
            <Box display="flex" gap={1} flexWrap={'wrap'}>
              <Button
                onClick={() => expandCollapseBatched(treeRef.current!, {open: true})}
                size="small"
                variant="text"
                startIcon={<UnfoldMoreIcon />}
                sx={{p: 0.5}}
              >
                {t('plantSetup.action.expand')}
              </Button>
              <Button
                onClick={async () => {
                  await expandCollapseBatched(treeRef.current!, {
                    open: false,
                    excludeIds: [plantId]
                  })
                  treeRef.current?.open(plantId)
                }}
                size="small"
                variant="text"
                startIcon={<UnfoldLessIcon />}
                sx={{p: 0.5}}
              >
                {t('plantSetup.action.collapse')}
              </Button>
            </Box>
          </Box>
          <Box ref={treeContainerRef} height={'55vh'} {...dataTestId('tree-container')}>
            {arboristTree && (
              <Tree
                ref={treeRef}
                data={arboristTree}
                openByDefault={false}
                width={width}
                rowHeight={56}
                height={height}
                indent={20}
                searchTerm={searchTerm}
                searchMatch={handleTreeFiltering}
                idAccessor={(node) => node.upmId}
                selection={nodeIdParam}
                onSelect={(nodes) => {
                  if (nodes[0] && nodeIdParam && nodes[0].data.upmId !== nodeIdParam) {
                    if (!selectionGuardRef.current) {
                      nodes[0].data.level = nodes[0].level
                      setSelectedItem(nodes[0].data)
                    }
                    const targetUrl = getUrl(PLANT_ROUTES.PLANT_SETUP.NODE.path, {
                      plantCode,
                      nodeId: nodes[0].data.upmId
                    })
                    const searchParamsString = searchParams.toString()
                    const urlWithParams = searchParamsString
                      ? `${targetUrl}?${searchParamsString}`
                      : targetUrl
                    void navigate(urlWithParams)
                  }
                }}
                initialOpenState={{[plantId]: true}}
              >
                {wrappedNodeComponent}
              </Tree>
            )}
          </Box>
        </Stack>
      </Box>
      <AddSubdomain />
      <PlantStructureTreePopupWrapper
        nodeType={departmentToDelete}
        popup={<DeleteDepartmentPopup />}
      />
      <PlantStructureTreePopupWrapper
        nodeType={equipmentToDelete}
        popup={<DeleteEquipmentPopup />}
      />
      <PlantStructureTreePopupWrapper
        nodeType={departmentToRestore}
        popup={<RestoreDepartmentPopup />}
      />
      <PlantStructureTreePopupWrapper
        nodeType={equipmentToRestore}
        popup={<RestoreEquipmentPopup />}
      />
      <PlantStructureTreePopupWrapper nodeType={editedArea} popup={<AddDepartmentPopup />} />
      <PlantStructureTreePopupWrapper
        nodeType={addedArea && upmIdNeededForAddOperations.data?.id ? addedArea : null}
        popup={
          <AddAreaPopup
            upmPlantId={upmIdNeededForAddOperations.data?.id as string}
            existingAreas={existingAreas}
          />
        }
      />
    </>
  )
}
